import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
/**
 * Modal accesible: portal en document.body, focus trap, scroll lock
 * con compensación de scrollbar, cierre con Escape/overlay y restore focus.
 */
const SIZE_CLASSES = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-[90vw] max-h-[90vh]",
};
function focusableElements(root: Element | null): HTMLElement[] {
    if (!root)
        return [];
    return Array.from(root.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')) as HTMLElement[];
}
export default function Modal({ open, onClose, title, children, footer = null, size = "md", zIndexClass = "z-50", closeOnEscape = true, }: {
    open: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    size?: keyof typeof SIZE_CLASSES;
    zIndexClass?: string;
    closeOnEscape?: boolean;
}): any {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const previousActiveRef = useRef<Element | null>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    // Solo al cambiar `open` (o closeOnEscape): no incluir `onClose` en dependencias — suele ser una
    // función nueva en cada render del padre y re-ejecuta el efecto, volviendo a enfocar el 1.º input.
    useEffect((): any => {
        if (!open)
            return undefined;
        previousActiveRef.current = document.activeElement;
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;
        const prevOverflow = document.body.style.overflow;
        const prevPaddingRight = document.body.style.paddingRight;
        document.body.style.overflow = "hidden";
        if (scrollbar > 0) {
            document.body.style.paddingRight = `${scrollbar}px`;
        }
        const handleKey = (e: any): any => {
            if (closeOnEscape && e.key === "Escape") {
                e.stopPropagation();
                onCloseRef.current?.();
                return;
            }
            if (e.key === "Tab" && panelRef.current) {
                const focusables = focusableElements(panelRef.current);
                if (focusables.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
                else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener("keydown", handleKey);
        const t = window.setTimeout((): any => {
            const focusables = focusableElements(panelRef.current);
            const target = focusables.find((el: any): any => el.tagName === "INPUT" ||
                el.tagName === "TEXTAREA" ||
                el.tagName === "SELECT") ?? focusables[0];
            target?.focus();
        }, 50);
        return (): any => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = prevOverflow;
            document.body.style.paddingRight = prevPaddingRight;
            window.clearTimeout(t);
            const prev = previousActiveRef.current;
            if (prev instanceof HTMLElement)
                prev.focus();
        };
    }, [open, closeOnEscape]);
    if (!open)
        return null;
    if (typeof document === "undefined")
        return null;
    const overlay = (<div className={`fixed inset-0 ${zIndexClass} flex items-center justify-center p-4 animate-in fade-in duration-150`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="absolute inset-0 bg-fg/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true"/>
      <div ref={panelRef} className={`relative w-full ${SIZE_CLASSES[size] ?? SIZE_CLASSES.md} max-h-[90vh] flex flex-col rounded-modal bg-surface shadow-xl border border-border overflow-hidden animate-in zoom-in-95 duration-150`} onClick={(e: any): any => e.stopPropagation()}>
        <div className="flex items-center justify-between shrink-0 px-6 py-4 border-b border-border bg-surface-sunken">
          <h2 id="modal-title" className="text-lg font-semibold text-fg">
            {title}
          </h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-fg-subtle hover:bg-border hover:text-fg transition-colors focus:outline-none focus:ring-2 focus:ring-border-strong focus:ring-offset-2" aria-label="Cerrar">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
        {footer != null && (<div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-surface-sunken">
            {footer}
          </div>)}
      </div>
    </div>);
    return createPortal(overlay, document.body);
}
